<template>
  <div id="app">
    <form
      method="post"
      action="/register/"
      ref="form"
      :class="{ invalid: invalid }"
      @submit.prevent="submit"
      @invalid.capture="invalid = true">
      <div class="columns is-desktop">
        <div class="column is-two-thirds-desktop">
          <input-field name="name" label="Name" v-model="name" required />
          <input-field name="surname" label="Surname" v-model="surname" required />
          <input-field
            name="email"
            label="E-mail"
            v-model="email"
            type="email"
            required />
          <input-field name="phone" label="Phone" v-model="phone" type="tel" />

          <input-field name="paper_id" label="Paper ID" v-model="paperId">
            <p class="help">
              If you have more papers, separated their IDs by commas, e.g.
              <kbd>16,40</kbd>
            </p>
          </input-field>

          <HorizontalField name="extra_pages" label="Extra pages">
            <input
              id="extra_pages"
              name="extra_pages"
              v-model.number="extra_pages"
              type="number"
              min="0"
              max="6"
              step="1"
              class="input is-short" />
            <template v-slot:info>
              <p class="help">
                Fill if your paper exceeds 6 pages. Extra pages cost 10€ per page.
              </p>
            </template>
          </HorizontalField>
          <div class="field">
            <div class="control">
              <label class="checkbox">
                <input type="checkbox" name="proceedings" v-model="proceedings" />
                Printed proceedings (20€)
              </label>
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label class="checkbox">
                <input
                  type="checkbox"
                  name="accompanying_person"
                  v-model="accompanying" />
                Include an accompanying person (150€)
              </label>
              <p class="help">
                Accompanying persons are individuals who join conference participants.
                They do not attend the conference sessions but can participate in the
                social events, including the trip.
              </p>
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label class="checkbox">
                <input type="checkbox" name="ieee_member" v-model="ieee_member" />
                I am IEEE member (discount 50€)
              </label>
            </div>
          </div>

          <VoucherField @update:discountPercentage="handleDiscountUpdate" />

          <h2 class="title is-4">Invoice Information</h2>
          <p class="help">
            The details you provide below (address, CIN, TIN, VAT numbers) will appear
            on your invoice.
          </p>

          <HorizontalField name="address" class="is-required has-long-label">
            <template v-slot:label>
              <label for="address" class="label">Paying institution</label>
              <p class="help">Name and address of the paying institution or person.</p>
            </template>
            <textarea
              v-model="address"
              id="address"
              name="address"
              class="textarea"
              rows="6"
              required></textarea>
          </HorizontalField>

          <input-field
            name="cin"
            label="CIN (Commerce ID Number) / IČO"
            v-model="cin"
            class="has-long-label" />
          <input-field
            name="tin"
            label="TIN (Tax ID Number) / DIČ"
            v-model="tin"
            class="has-long-label" />
          <input-field
            name="vat"
            label="VAT Number / IČ DPH"
            v-model="vat"
            class="has-long-label" />

          <h2 class="title is-4">Trip</h2>
          <div class="field">
            <radio-group name="trip" :options="tripOptions" v-model="trip" />
          </div>
          <h2 class="title is-4">Food</h2>
          <FoodSelection />

          <h2 class="title is-4"><label for="note">Additional Notes</label></h2>

          <div class="field">
            <div class="control">
              <textarea id="note" name="note" class="textarea"></textarea>
            </div>
            <p class="help">E.g. vegetarian food, allergies, etc</p>
          </div>
        </div>
        <div class="column">
          <div class="sticky">
            <h2 class="title is-4">Summary</h2>

            <fee-summary
              :base-fee="baseFee"
              :discounts="discounts"
              :extra-fees="extraFees"
              :accompanying-person="accompanyingFee"
              :total-fee="totalFee" />
            <input type="hidden" name="fee" :value="totalFee" />

            <p class="help">
              The information provided in this form will be used for organizing the
              conference and other related services. We may also use your email address
              to inform you about important conference-related events.
            </p>
            <p class="help">
              Conference organizers will take photographs during the event for
              promotional purposes. These photos may be published on the conference
              website.
            </p>
            <div class="field">
              <p v-if="errors" class="is-danger">
                There are errors in the form: {{ errors }}
              </p>
              <div class="control">
                <button
                  type="submit"
                  class="button is-link is-medium"
                  :class="{ 'is-loading': submitted }">
                  Submit Registration
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import axios from "axios";
import HorizontalField from "./components/HorizontalField";
import InputField from "./components/InputField";
import RadioGroup from "./components/RadioGroup";
import FeeSummary from "./components/FeeSummary";
import FoodSelection from "./components/FoodSelection";
import VoucherField from "./components/VoucherField";

const noTrip = {
  id: "",
  title: "No trip",
  limited: false,
  available: null,
};

export default {
  name: "app",
  components: {
    FoodSelection,
    HorizontalField,
    InputField,
    RadioGroup,
    FeeSummary,
    VoucherField,
  },
  data() {
    return {
      name: "",
      surname: "",
      email: "",
      phone: "",
      paperId: "",
      extra_pages: 0,
      proceedings: false,
      ieee_member: false,
      accompanying: false,
      discountPercentage: 0,
      address: "",
      cin: "",
      tin: "",
      vat: "",
      trip: noTrip,
      tripOptions: [noTrip],
      submitted: false,
      errors: null,
      invalid: false,
    };
  },
  computed: {
    baseFee: function () {
      return 350;
    },
    discounts: function () {
      if (this.discountPercentage === 100)
        return -(this.baseFee + this.extraFees + this.accompanyingFee);
      const ieeeDiscount = this.ieee_member ? 50 : 0;
      const voucherDiscount =
        (this.baseFee - ieeeDiscount) * (this.discountPercentage / 100);
      return -(ieeeDiscount + voucherDiscount);
    },
    extraFees: function () {
      return this.extra_pages * 10 + (this.proceedings ? 20 : 0);
    },
    accompanyingFee: function () {
      return this.accompanying ? 150 : 0;
    },
    totalFee: function () {
      if (this.voucherDiscount === 100) return 0;
      return this.baseFee + this.discounts + this.extraFees + this.accompanyingFee;
    },
  },
  methods: {
    handleDiscountUpdate(discount) {
      if (discount) this.discountPercentage = discount;
      else this.discountPercentage = 0;
    },
    submit: function (e) {
      let form = this.$refs.form;
      let formData = new FormData(form);
      axios
        .post("/register/", formData, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
        })
        .then((response) => {
          window.location = response.data.location;
        })
        .catch((error) => {
          if (error.response) {
            this.errors = error.response.data;
          } else {
            this.errors = "Some error encountered while sending the form.";
          }
          this.submitted = false;
        });
      this.submitted = true;
    },
  },
  mounted() {
    axios.get("/register/options/").then((response) => {
      this.tripOptions.push(...response.data.trips);
      setTripTitles(this.tripOptions);
    });
  },
};

function setTripTitles(trips) {
  for (const trip of trips) {
    if (trip.id) {
      if (trip.available !== null) {
        trip.title = `${trip.title} (${trip.available} available)`;
      }
    }
  }
}
</script>

<style>
.field-body {
  flex-grow: 4;
}
.is-short {
  max-width: 5em;
}
.help {
  font-size: 0.8125rem;
}
.invalid input:invalid,
.invalid textarea:invalid {
  border-color: #f14668;
}
.invalid input:invalid:focus,
.invalid input:invalid:active,
.invalid textarea:invalid:focus,
.invalid textarea:invalid:active {
  box-shadow: 0 0 0 0.125em rgba(241, 70, 104, 0.25);
}

.sticky {
  position: sticky;
  top: 0.5em;
}
</style>
